import { useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';
import {
  EVENT_STATUS_LABEL, EVENT_STATUS_PILL,
  ISSUE_STATUS_LABEL, ISSUE_STATUS_PILL,
  KIND_PILL, MAGENTA, SEVERITY_BAR,
  formatDateShort, formatFrequency, formatRelative, ticketNumber,
} from './maintenance-shared.js';
import { NewIssueModal } from './NewIssueModal.jsx';
import { NewEventModal } from './NewEventModal.jsx';
import { NewScheduleModal } from './NewScheduleModal.jsx';

const Eyebrow = () => (
  <div class="mb-1.5 flex items-center gap-2">
    <span class="block h-2.5 w-2.5 rounded-full" style={{ background: MAGENTA }} />
    <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: MAGENTA }}>
      Maintenance
    </span>
  </div>
);

const TabBar = ({ active, counts, onChange }) => (
  <div class="mb-4 flex border-b border-border-subtle">
    {[
      { v: 'issues',    label: 'Issues',    count: counts.issues },
      { v: 'log',       label: 'Log',       count: counts.events },
      { v: 'schedules', label: 'Schedules', count: counts.schedules },
    ].map((t) => (
      <button
        key={t.v}
        type="button"
        onClick={() => onChange(t.v)}
        class={`border-b-2 px-5 py-2.5 text-[13px] font-semibold ${
          active === t.v
            ? 'border-ink-magenta text-text-primary'
            : 'border-transparent text-text-tertiary hover:text-text-body'
        }`}
      >
        {t.label}
        {t.count != null && <span class="ml-2 text-[11px] text-text-muted">{t.count}</span>}
      </button>
    ))}
  </div>
);

const Chip = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    class={`rounded-full border px-3 py-1 text-[11px] font-semibold ${
      active
        ? 'border-ink-magenta bg-ink-magenta/[0.12] text-ink-magenta'
        : 'border-border-strong text-text-tertiary hover:bg-bg-card'
    }`}
  >
    {children}
  </button>
);

// Matches the pattern StatisticsPage.jsx uses for its own async error state.
const Failed = ({ error }) => (
  <p class="py-6 text-center text-sm text-text-muted">
    Couldn’t load — {error?.message || 'request failed'}
    {error?.status ? ` (${error.status})` : ''}.
  </p>
);

const MachineChip = ({ name, kind }) => (
  <span class="inline-flex items-center gap-1.5 rounded-full border border-border-strong bg-bg-base px-2 py-0.5 text-[10px] font-semibold text-text-tertiary">
    <span class="block h-1.5 w-1.5 rounded-full bg-ink-teal" />
    {name ?? '—'}
  </span>
);

const IssueRow = ({ issue, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(issue.id)}
    class="grid w-full grid-cols-[3px_minmax(0,1fr)_auto_auto_auto] items-center gap-3 border-b border-border-subtle px-4 py-3 text-left last:border-b-0 hover:bg-bg-hover"
  >
    <span class={`block h-9 w-1 rounded-sm ${SEVERITY_BAR[issue.severity] ?? 'bg-border-strong'}`} aria-hidden="true" />
    <div class="min-w-0">
      <p class="m-0 flex items-center gap-2 truncate font-serif text-[15px] font-medium text-text-primary">
        <span class="font-mono text-[11px] font-semibold text-text-muted">{ticketNumber(issue)}</span>
        {issue.title}
      </p>
      <p class="m-0 text-[11px] font-medium text-text-tertiary">
        {issue.reporterName ?? 'Unknown'} · {formatRelative(issue.createdAt)}
        {(issue.assigneeName || issue.assigneeContact) && (
          <span> · Assigned: {[issue.assigneeName, issue.assigneeContact].filter(Boolean).join(' + ')}</span>
        )}
      </p>
    </div>
    <MachineChip name={issue.machineName} />
    {issue.affectsProduction && (
      <span class="rounded-full border border-ink-magenta/[0.3] bg-ink-magenta/[0.12] px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] text-ink-magenta">
        Affects prod
      </span>
    )}
    {!issue.affectsProduction && <span />}
    <span class={`rounded-full border px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] ${ISSUE_STATUS_PILL[issue.status]}`}>
      {ISSUE_STATUS_LABEL[issue.status]}
    </span>
  </button>
);

const EventRow = ({ event, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(event.id)}
    class="grid w-full grid-cols-[3px_minmax(0,1fr)_auto_auto_auto_auto] items-center gap-3 border-b border-border-subtle px-4 py-3 text-left last:border-b-0 hover:bg-bg-hover"
  >
    <span class={`block h-9 w-1 rounded-sm ${event.kind === 'visit' ? 'bg-status-purple' : 'bg-status-ok'}`} aria-hidden="true" />
    <div class="min-w-0">
      <p class="m-0 flex items-center gap-2 truncate font-serif text-[15px] font-medium text-text-primary">
        <span class="font-mono text-[11px] font-semibold text-text-muted">{ticketNumber(event)}</span>
        {event.title}
      </p>
      <p class="m-0 text-[11px] font-medium text-text-tertiary">
        {event.performedByName ?? event.technicianName ?? 'Unassigned'}
      </p>
    </div>
    <MachineChip name={event.machineName} />
    <span class={`rounded-full border px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] ${KIND_PILL[event.kind]}`}>
      {event.kind === 'routine' ? 'Routine' : 'Visit'}
    </span>
    <span class={`rounded-full border px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] ${EVENT_STATUS_PILL[event.status]}`}>
      {EVENT_STATUS_LABEL[event.status]}
    </span>
    <span class="min-w-[60px] text-right text-[11px] font-medium text-text-muted">
      {formatDateShort(event.performedAt ?? event.scheduledAt ?? event.createdAt)}
    </span>
  </button>
);

const ScheduleRow = ({ schedule, onOpen, onToggle }) => (
  <div class="grid grid-cols-[3px_minmax(0,1fr)_auto_auto_auto_auto] items-center gap-3 border-b border-border-subtle px-4 py-3 last:border-b-0">
    <span class={`block h-9 w-1 rounded-sm ${schedule.kind === 'visit' ? 'bg-status-purple' : 'bg-status-ok'}`} aria-hidden="true" />
    <button type="button" onClick={() => onOpen(schedule.id)} class="min-w-0 text-left">
      <p class="m-0 truncate font-serif text-[15px] font-medium text-text-primary">{schedule.title}</p>
      <p class="m-0 text-[11px] font-medium text-text-tertiary">{formatFrequency(schedule)}</p>
    </button>
    <MachineChip name={schedule.machineName} />
    <span class={`rounded-full border px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] ${KIND_PILL[schedule.kind]}`}>
      {schedule.kind === 'routine' ? 'Routine' : 'Visit'}
    </span>
    <button
      type="button"
      onClick={() => onToggle(schedule)}
      class={`rounded-full border px-2 py-[2px] text-[10px] font-bold uppercase tracking-[0.04em] ${
        schedule.active
          ? 'border-status-ok/[0.3] bg-status-ok/[0.12] text-status-ok'
          : 'border-border-strong text-text-tertiary'
      }`}
    >
      {schedule.active ? 'Active' : 'Inactive'}
    </button>
    <span class="min-w-[80px] text-right text-[11px] font-medium text-text-muted">
      Next: {formatDateShort(schedule.nextDueAt)}
    </span>
  </div>
);

export const MaintenanceIndexPage = () => {
  const { route } = useLocation();
  const initialTab = (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('tab')) || 'issues';
  const [tab, setTab] = useState(initialTab);

  const [issueFilter, setIssueFilter] = useState('open');
  const [logFilter, setLogFilter] = useState('all');

  const issueQuery = (() => {
    if (issueFilter === 'open') return '?status=open,in_progress,scheduled';
    if (issueFilter === 'affects') return '?affectsProduction=true&status=open,in_progress,scheduled';
    return '';
  })();
  const eventQuery = (() => {
    if (logFilter === 'routine') return '?kind=routine';
    if (logFilter === 'visit') return '?kind=visit';
    if (logFilter === 'missed') return '?status=missed';
    return '';
  })();

  const { data: issues, error: issuesError, refetch: refetchIssues } = useApi(`/maintenance/issues${issueQuery}`);
  const { data: events, error: eventsError, refetch: refetchEvents } = useApi(`/maintenance/events${eventQuery}`);
  const { data: schedules, error: schedulesError, refetch: refetchSchedules } = useApi('/maintenance/schedules');

  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [newIssueOpen, setNewIssueOpen] = useState(false);
  const [newEventOpen, setNewEventOpen] = useState(false);
  const [newScheduleOpen, setNewScheduleOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);

  const counts = useMemo(() => ({
    issues: issues?.length,
    events: events?.length,
    schedules: schedules?.length,
  }), [issues, events, schedules]);

  const openIssue = (id) => { route(`/maintenance/issues/${id}`); };
  const openEvent = (id) => { route(`/maintenance/events/${id}`); };
  const openSchedule = (id) => {
    const s = (schedules ?? []).find((x) => x.id === id);
    if (s) setEditingSchedule(s);
  };

  const toggleSchedule = async (s) => {
    try {
      await api.patch(`/maintenance/schedules/${s.id}`, { active: !s.active });
      refetchSchedules();
    } catch (err) {
      alert(err.message ?? 'Failed to update');
    }
  };

  return (
    <div>
      <section class="mb-6 flex items-start justify-between gap-4">
        <div>
          <Eyebrow />
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">
            Tickets &amp; tasks
          </h1>
          <p class="m-0 text-sm text-text-secondary">
            Issues, maintenance logs, and recurring schedules across all equipment.
          </p>
        </div>
      </section>

      <TabBar active={tab} counts={counts} onChange={setTab} />

      {tab === 'issues' && (
        <>
          <div class="mb-4 flex flex-wrap items-center gap-2">
            <Chip active={issueFilter === 'open'} onClick={() => setIssueFilter('open')}>Open</Chip>
            <Chip active={issueFilter === 'affects'} onClick={() => setIssueFilter('affects')}>Affects production</Chip>
            <Chip active={issueFilter === 'all'} onClick={() => setIssueFilter('all')}>All</Chip>
            <Button variant="primary" onClick={() => setNewIssueOpen(true)} class="ml-auto">+ New issue</Button>
          </div>
          {issuesError && <Failed error={issuesError} />}
          {!issuesError && issues == null && <p class="text-sm text-text-tertiary">Loading…</p>}
          {!issuesError && issues && issues.length === 0 && (
            <div class="surface-card p-6 text-center">
              <p class="m-0 font-serif text-lg font-medium text-text-primary">No issues here.</p>
              <p class="m-0 mt-1 text-sm text-text-secondary">Open one with "+ New issue".</p>
            </div>
          )}
          {!issuesError && issues && issues.length > 0 && (
            <div class="overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
              {issues.map((i) => <IssueRow key={i.id} issue={i} onOpen={openIssue} />)}
            </div>
          )}
        </>
      )}

      {tab === 'log' && (
        <>
          <div class="mb-4 flex flex-wrap items-center gap-2">
            <Chip active={logFilter === 'all'} onClick={() => setLogFilter('all')}>All</Chip>
            <Chip active={logFilter === 'routine'} onClick={() => setLogFilter('routine')}>Routine</Chip>
            <Chip active={logFilter === 'visit'} onClick={() => setLogFilter('visit')}>Visit</Chip>
            <Chip active={logFilter === 'missed'} onClick={() => setLogFilter('missed')}>Missed</Chip>
            <Button variant="primary" onClick={() => setNewEventOpen(true)} class="ml-auto">+ Log event</Button>
          </div>
          {eventsError && <Failed error={eventsError} />}
          {!eventsError && events == null && <p class="text-sm text-text-tertiary">Loading…</p>}
          {!eventsError && events && events.length === 0 && (
            <div class="surface-card p-6 text-center">
              <p class="m-0 font-serif text-lg font-medium text-text-primary">No events here.</p>
              <p class="m-0 mt-1 text-sm text-text-secondary">Add a maintenance entry with "+ Log event".</p>
            </div>
          )}
          {!eventsError && events && events.length > 0 && (
            <div class="overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
              {events.map((e) => <EventRow key={e.id} event={e} onOpen={openEvent} />)}
            </div>
          )}
        </>
      )}

      {tab === 'schedules' && (
        <>
          <div class="mb-4 flex flex-wrap items-center gap-2">
            {isAdmin && (
              <Button variant="primary" onClick={() => setNewScheduleOpen(true)} class="ml-auto">+ New schedule</Button>
            )}
          </div>
          {schedulesError && <Failed error={schedulesError} />}
          {!schedulesError && schedules == null && <p class="text-sm text-text-tertiary">Loading…</p>}
          {!schedulesError && schedules && schedules.length === 0 && (
            <div class="surface-card p-6 text-center">
              <p class="m-0 font-serif text-lg font-medium text-text-primary">No recurring schedules.</p>
              <p class="m-0 mt-1 text-sm text-text-secondary">Create one to auto-track recurring maintenance.</p>
            </div>
          )}
          {!schedulesError && schedules && schedules.length > 0 && (
            <div class="overflow-hidden rounded-xl border border-border-subtle bg-bg-card">
              {schedules.map((s) => (
                <ScheduleRow key={s.id} schedule={s} onOpen={openSchedule} onToggle={toggleSchedule} />
              ))}
            </div>
          )}
        </>
      )}

      <NewIssueModal open={newIssueOpen} onClose={() => setNewIssueOpen(false)} onCreated={() => { setNewIssueOpen(false); refetchIssues(); }} />
      <NewEventModal open={newEventOpen} onClose={() => setNewEventOpen(false)} onCreated={() => { setNewEventOpen(false); refetchEvents(); }} />
      <NewScheduleModal open={newScheduleOpen} onClose={() => setNewScheduleOpen(false)} onCreated={() => { setNewScheduleOpen(false); refetchSchedules(); }} />
      <NewScheduleModal
        open={!!editingSchedule}
        schedule={editingSchedule}
        onClose={() => setEditingSchedule(null)}
        onSaved={() => { setEditingSchedule(null); refetchSchedules(); }}
      />
    </div>
  );
};
